import { useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Popover,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import EmojiPicker from "emoji-picker-react";
import useNotifications from "./useNotifications";
import { styles } from "./notifications.styles";

const CHANNEL_LABELS = {
  general: "Todas las usuarias",
  premium: "Usuarias con membresía",
  live: "Suscriptoras a Vivos",
  likes: "Suscriptoras a Likes",
  reposts: "Suscriptoras a Reposts",
  mentions: "Suscriptoras a Menciones",
};

const Notifications = () => {
  const theme = useTheme();
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [emojiTarget, setEmojiTarget] = useState(null);
  const titleRef = useRef(null);
  const bodyRef = useRef(null);

  const {
    channels,
    selectedChannel,
    setSelectedChannel,
    title,
    setTitle,
    body,
    setBody,
    loading,
    result,
    setResult,
    error,
    setError,
    history,
    sendNotification,
    canSend,
  } = useNotifications();

  return (
    <Box sx={styles.container}>
      {/* Form Panel */}
      <Paper elevation={0} sx={styles.formPanel}>
        <Box sx={styles.formHeader}>
          <NotificationsActiveIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Nueva Notificación
          </Typography>
        </Box>

        <Box sx={styles.formContent}>
          {/* Channel Selection */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Destinatarias
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {channels.map((ch) => (
                <Chip
                  key={ch.slug}
                  label={ch.name}
                  variant="outlined"
                  onClick={() => setSelectedChannel(ch.slug)}
                  sx={styles.channelChip(
                    selectedChannel === ch.slug,
                    theme
                  )}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
              {CHANNEL_LABELS[selectedChannel] || `Canal: ${selectedChannel}`}
            </Typography>
          </Box>

          {/* Title */}
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Título"
              placeholder="Ej: Nuevo contenido disponible"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              variant="outlined"
              inputRef={titleRef}
              inputProps={{ maxLength: 100 }}
              helperText={`${title.length}/100`}
              InputProps={{
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setEmojiAnchor(e.currentTarget);
                      setEmojiTarget("title");
                    }}
                  >
                    <EmojiEmotionsIcon sx={{ color: "rgba(0,0,0,0.4)" }} />
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* Body */}
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Mensaje"
              placeholder="Escribí el mensaje que van a recibir las usuarias..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              inputRef={bodyRef}
              inputProps={{ maxLength: 500 }}
              helperText={`${body.length}/500`}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                setEmojiAnchor(e.currentTarget);
                setEmojiTarget("body");
              }}
              sx={{ position: "absolute", right: 8, bottom: 28 }}
            >
              <EmojiEmotionsIcon sx={{ color: "rgba(0,0,0,0.4)" }} />
            </IconButton>
          </Box>

          {/* Emoji Picker */}
          <Popover
            open={Boolean(emojiAnchor)}
            anchorEl={emojiAnchor}
            onClose={() => setEmojiAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <EmojiPicker
              onEmojiClick={(emojiData) => {
                const emoji = emojiData.emoji;
                if (emojiTarget === "title") {
                  setTitle((prev) => prev + emoji);
                } else {
                  setBody((prev) => prev + emoji);
                }
                setEmojiAnchor(null);
              }}
              searchPlaceHolder="Buscar emoji..."
              width={350}
              height={400}
            />
          </Popover>

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Send Button */}
          <Button
            variant="contained"
            size="large"
            onClick={sendNotification}
            disabled={!canSend}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            sx={styles.sendButton(theme)}
          >
            {loading ? "Enviando..." : "Enviar Notificación"}
          </Button>
        </Box>
      </Paper>

      {/* History Panel */}
      <Paper elevation={0} sx={styles.historyPanel}>
        <Box sx={styles.historyHeader}>
          <HistoryIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography variant="subtitle2" color="text.secondary">
            Enviadas esta sesión
          </Typography>
          <Chip
            label={history.length}
            size="small"
            sx={{ height: 20, fontSize: "0.7rem", bgcolor: "rgba(0,0,0,0.06)" }}
          />
        </Box>

        <Box sx={styles.historyList}>
          {history.length === 0 ? (
            <Box sx={styles.emptyHistory}>
              <NotificationsNoneIcon sx={{ fontSize: 48 }} />
              <Typography variant="body2">
                Las notificaciones enviadas aparecerán aquí
              </Typography>
            </Box>
          ) : (
            history.map((item, index) => (
              <Box key={index} sx={styles.historyItem}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {item.sentAt}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {item.body}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    label={item.channel}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 22 }}
                  />
                  {item.sent !== undefined && (
                    <Chip
                      label={`${item.sent} enviadas`}
                      size="small"
                      sx={{
                        fontSize: "0.7rem",
                        height: 22,
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                        color: "#4caf50",
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Paper>

      {/* Success Alert */}
      {result && (
        <Alert
          severity="success"
          sx={styles.successAlert}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setResult(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <AlertTitle>Notificación enviada</AlertTitle>
          Se envió a {result.sent} usuarias.
        </Alert>
      )}
    </Box>
  );
};

export default Notifications;
